import { IonCheckbox, IonModal } from "@ionic/react";
import { CSSProperties } from "react";
import { FC, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import styled from "styled-components";
import { useConfig } from "../../contexts/ConfigContext";
import useToast from "../../hooks/useToast";
import { leaveStudy } from "../../services/study.service";
import AuthContext from "../../contexts/AuthContext";
import { isPasswordSignInMethod } from "../../types/profile";
import { TextField } from "../../components/atoms/LoginTextField";
import {
  ModalOverlay,
  ModalCard,
  ModalTitle,
  ModalDescription,
  ModalActions,
  ModalFields,
  ModalPrimaryButton,
  ModalQuietButton,
} from "../../components/atoms/ModalKit";

interface Props {
  isOpen: boolean;
  onDismiss(): void;
}

const LeaveStudyModal: FC<Props> = ({ isOpen, onDismiss }) => {
  const { t } = useTranslation();
  const [allow_access_data, set_allow_access_data] = useState(true);
  const [currentPassword, setCurrentPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | undefined>(
    undefined
  );
  const [isCurrentPasswordFieldUnlocked, setIsCurrentPasswordFieldUnlocked] =
    useState(false);
  const history = useHistory();
  const { study, selectedRole } = useConfig();
  const { study_slug } = useParams<{ study_slug: string }>();
  const present = useToast();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { user, endSession } = useContext(AuthContext);

  const isPasswordUser = useMemo(
    () => isPasswordSignInMethod(selectedRole?.access?.signInMode),
    [selectedRole]
  );

  useEffect(() => {
    if (!isOpen) return;
    setCurrentPassword("");
    setPasswordError(undefined);
    setIsCurrentPasswordFieldUnlocked(false);
    set_allow_access_data(true);
  }, [isOpen]);
  
  const handleConfirmOptOut = async () => {
    if (isPasswordUser && !currentPassword.trim()) {
      return;
    }

    setPasswordError(undefined);

    try {
      await leaveStudy(study.id, {
        allow_access_data,
        current_password: isPasswordUser ? currentPassword : undefined,
      });
      await endSession();
      history.push(`/${study_slug}/welcome`);
    } catch (e) {
      if (isPasswordUser && e?.response?.status === 401) {
        setPasswordError("profile_change_email_invalid_password");
        return;
      }

      present(e?.response?.data?.message);
    }
  };

  return (
    <IonModal
      className="resonance-modal"
      isOpen={isOpen}
      backdropDismiss={true}
      onWillDismiss={onDismiss}
    >
      <ModalOverlay onClick={onDismiss}>
        <ModalCard onClick={(e) => e.stopPropagation()}>
          <ModalTitle>{t("mentalplus_account_deactivate")}</ModalTitle>
          <ModalDescription>
            {t("mentalplus_account_deactivate_message")}
          </ModalDescription>

          <Question>
            <IonCheckbox
              style={{ marginRight: 10 }}
              onIonChange={(e) => set_allow_access_data(e.detail.checked)}
              checked={allow_access_data}
              mode="md"
            />
            {t("profile_allow_access_data")}
          </Question>

          {isPasswordUser && (
            <ModalFields>
              <TextField
                value={currentPassword}
                type="text"
                onChange={(value) => {
                  setCurrentPassword(value);
                  if (passwordError) {
                    setPasswordError(undefined);
                  }
                }}
                label={t("change_pwd_old_password")}
                placeholder={t("change_pwd_old_password")}
                error={passwordError}
                autoComplete="off"
                inputProps={{
                  name: "profile-deactivate-verification-value",
                  readOnly: !isCurrentPasswordFieldUnlocked,
                  onFocus: () => setIsCurrentPasswordFieldUnlocked(true),
                  onPointerDown: () => setIsCurrentPasswordFieldUnlocked(true),
                  autoCapitalize: "none",
                  autoCorrect: "off",
                  spellCheck: false,
                  style: { WebkitTextSecurity: "disc" } as CSSProperties,
                  "data-lpignore": "true",
                  "data-1p-ignore": "true",
                  "data-bwignore": "true",
                  "data-form-type": "other",
                }}
              />
            </ModalFields>
          )}

          <ModalActions>
            <ModalQuietButton type="button" onClick={onDismiss}>
              {t("common_cancel")}
            </ModalQuietButton>
            <ModalPrimaryButton
              type="button"
              onClick={handleConfirmOptOut}
              disabled={isPasswordUser && !currentPassword.trim()}
            >
              {t("confirm_quit_study_button")}
            </ModalPrimaryButton>
          </ModalActions>
        </ModalCard>
      </ModalOverlay>
    </IonModal>
  );
};

const Question = styled.div`
  display: flex;
  align-items: center;
  padding: 5px 0;
  font-size: 14px;
  color: var(--app-color-text, #041723);
`;


export default LeaveStudyModal;
